import { useState } from "react";
import React, { createContext, FC } from "react";

import useLocalStorage from "@rehooks/local-storage";
import { Configuration, User } from "@contractool/schema";

import {
  getFetching,
  getReadiness,
  init as initTranslations,
} from "@/utils/translations";

import { make } from "./url";
import { useRequest } from "./hooks/useRequest";
import { registerErrorResponseInterceptor } from "./http";

export const USER_KEY = "user";

export const AuthContext = createContext<{
  config: any;
  user: null | User;
  login: (user: User) => void;
  logout: () => void;
  update: (user: User) => void;
}>({
  config: null,
  user: null,
  login: () => {},
  logout: () => {},
  update: () => {},
});

export const AuthContextProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = Object.assign({}, useAuthHandler());
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuthHandler = () => {
  // Remove the localStorage dependency and use StorageManager instead
  const [user, setUser, unsetUser] = useLocalStorage<User>(USER_KEY);

  const [errorResponse, setErrorResponse] = useState({});
  const [configuration, { refresh }] = useRequest<Configuration | undefined>(
    "/api/configuration",
    undefined,
    {},
    setErrorResponse
  );
  const config = configuration === undefined ? errorResponse : configuration;

  if (!getFetching() && !getReadiness()) {
    initTranslations();
  }

  registerErrorResponseInterceptor((response) => {
    if (response?.status === 401) {
      unsetUser();
    }
  });

  return {
    config,

    user,

    login(user: User) {
      setUser(user);
      refresh();
      initTranslations();
    },

    logout() {
      // Remove token from storage
      OfficeRuntime.storage.removeItem("token");
      unsetUser();
    },

    update(user: User) {
      setUser(user);
    },
  };
};

export function sign(url: string): string {
  return make(url, { token: "" });
}
